import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { setSelectedProduct } from '../../../store/action/OrderAction';
import { trackTfnClick } from '../../../common/GTMScripts';
import EarthlinkPerks from './EarthlinkPerks';
import AddressHeading from './AddressHeading';
import { useServiceablitityData } from '../../../hooks/order';
import { resolveServiceDesc } from '../../../helpers/planTranslations';
import { isATTVendor, isTMOVendor } from '../../../util/util';
import Whi2 from '../../../images/whi-2.png';
import Whi3 from '../../../images/whi-3.png';
import Whi4 from '../../../images/whi-4.png';

// Icons/Images placeholders - In a real scenario, these would be imported from images/index.js
// Since some are missing, I'll reuse available ones or assume they will be added.
import { NoCreditChecks } from '../../../images';

const WhiPlans = (props) => {
  const { t } = useTranslation();
  const {
    fixedWirelessProducts,
    showCallNow,
    phoneNumber,
    isInternetAdded,
    internetPlan,
    addIntToCart,
    dispatch,
    getAggregatedAddress,
    normalizedAddress,
    onResetAddress,
    isSelected,
    isBusinessAddress,
    spanishLanguagePref,
  } = props;
  const { isExistingCustomer, fixedWireless } = useServiceablitityData();
  const isTmoVendor = (fixedWireless?.vendor === 'TMO');

  const product = fixedWirelessProducts && fixedWirelessProducts[0];

  if (!product) {return null;}

  const whiPerkItems = [
    {
      title: t('products.perkNoCreditTitle'),
      desc: t('products.perkNoCreditDesc'),
      icon: NoCreditChecks,
    },
    {
      title: t('whi.perkShippingTitle'),
      desc: t('whi.perkShippingDesc'),
      icon: Whi2,
    },
    {
      title: t('whi.perkNoContractsTitle'),
      desc: t('whi.perkNoContractsDesc'),
      icon: Whi3,
    },
    {
      title: t('whi.perkSelfInstallTitle'),
      desc: t('whi.perkSelfInstallDesc'),
      icon: Whi4,
    },
  ];

  let title = isBusinessAddress ? t('whi.titleBusiness5G') : t('products.title5GAvailable');
  let cardTitle = t('products.ctaTitle');
  let cardFooterText = t('products.existingCallNow');

  if (isExistingCustomer) {
    title = t('products.titleAlreadyConnected');
    cardTitle = t('products.existingContactTitle');
  } else {
    if (isBusinessAddress) {
      if (isTmoVendor && !product?.serviceName?.toLowerCase()?.includes('5g')) {
        title = t('whi.titleHighSpeedBusiness');
      } else {
        title = t('whi.titleBusiness5G');
      }
      cardFooterText = '';
    } else if (isTMOVendor(fixedWireless?.vendor)) {
      title = t('products.titleHighSpeedAvailable');
      cardFooterText = '';
    } else if (isATTVendor(fixedWireless?.vendor)) {
      title = t('products.title5GAvailable');
      cardFooterText = '';
    }
  }

  return (
    <div className="whi-container">
      <section className="whi-card-section">
        <div className="elink-container">
          <AddressHeading
            title= {title}
            address={getAggregatedAddress(normalizedAddress)}
            onResetAddress={onResetAddress}
            isExistingCustomer={isExistingCustomer}
            isInternetAdded={isInternetAdded}
            currentTfn={phoneNumber}
          />

          <div className="whi-card">
            <div className="most-popular-banner">{t('whi.mostPopularPlan')}</div>
            <div className="whi-card-content">
              <div className="left-col">
                <h2 className="whi-card-title">{product.serviceName?.replace('Plan', '')?.replace('plan', '')}</h2>
                <p className="whi-card-txt">
                  {resolveServiceDesc(
                    spanishLanguagePref,
                    product?.serviceDescriptionEsp,
                    product?.serviceDescription
                  )}
                </p>

                {showCallNow || isBusinessAddress ? (
                  <a
                    href={`tel:${phoneNumber}`}
                    className="blue-button call-button"
                    onClick={() => trackTfnClick({ productCategory: '5G' })}
                  >
                    <i className="fa-solid fa-phone-volume"></i> {phoneNumber}
                  </a>
                ) : (
                  <button
                    className={`blue-button call-button ${isInternetAdded ? 'grey-button' : ''}`}
                    disabled={isInternetAdded && internetPlan.servRef !== product.servRef}
                    onClick={() => {
                      addIntToCart(product);
                      dispatch(setSelectedProduct(product));
                    }}
                  >
                    <i
                      className={`fa-solid ${isSelected ? 'fa-solid fa-arrows-rotate fa-spin' : 'fa-solid fa-cart-shopping'
                      }`}
                    ></i>
                    {isInternetAdded && internetPlan.servRef === product.servRef ? t('common.addingToCart') : t('common.selectPlan')}
                  </button>
                )}
              </div>
              <div className="right-col">
                {isBusinessAddress ? (
                  <ul className="features-list">
                    {(!isTmoVendor || product?.serviceName?.toLowerCase()?.includes('5g')) ? (
                      <li className="feature-item">
                        <i className="fa-solid fa-gauge-high"></i>
                        <span>{t('whi.featureFastReliable')}</span>
                      </li>
                    ) : (
                      <li className="feature-item">
                        <i className="fa-solid fa-gauge-high"></i>
                        <span>{t('whi.featureFastReliableSecure')}</span>
                      </li>
                    )}
                    <li className="feature-item">
                      <i className="fa-solid fa-box-open"></i>
                      <span>{t('whi.featureFastShipping')}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-solid fa-wifi"></i>
                      <span>{t('whi.featureUnlimitedData')}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-solid fa-headset"></i>
                      <span>{t('whi.featureFreeBusinessTech')}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-solid fa-headset"></i>
                      <span>{t('whi.feature247Support')}</span>
                    </li>
                  </ul>
                ) : !isTmoVendor ? (
                  <ul className="features-list">
                    <li className="feature-item">
                      <i className="fa-solid fa-gauge-high"></i>
                      <span>{t('whi.featureFastest5G', { speed: product.downstreamSpd ? parseInt(product.downstreamSpd)/1000 : '425' })}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-solid fa-box-open"></i>
                      <span>{t('whi.featureSelfInstall')}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-solid fa-wifi"></i>
                      <span>{t('whi.featureUnlimitedDataMonth')}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-regular fa-calendar-days"></i>
                      <span>{t('whi.featureNorton')}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-solid fa-dollar-sign"></i>
                      <span>{t('whi.featureNoContracts')}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-solid fa-mobile-screen-button"></i>
                      <span>{t('whi.featureRemoteIT')}</span>
                    </li>
                  </ul>
                ) :  (
                  <ul className="features-list">
                    <li className="feature-item">
                      <i className="fa-solid fa-gauge-high"></i>
                      <span>{t('whi.featureUnlimitedDataMonth')}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-regular fa-calendar-days"></i>
                      <span>{t('whi.featureNorton')}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-solid fa-dollar-sign"></i>
                      <span>{t('whi.featureNoContracts')}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-solid fa-mobile-screen-button"></i>
                      <span>{t('whi.featureRemoteIT')}</span>
                    </li>
                    <li className="feature-item">
                      <i className="fa-solid fa-box-open"></i>
                      <span>{t('whi.featureSelfInstall')}</span>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <EarthlinkPerks
        phoneNumber={phoneNumber}
        perkItems={whiPerkItems}
        ctaTitle={cardTitle}
        ctaFooterText={cardFooterText}
      />


      {/* Keeping ReviewCarousel as requested or implies by structure */}
      {/* <ReviewCarousel showCallNow={showCallNow} /> */}
    </div>
  );
};

WhiPlans.propTypes = {
  fixedWirelessProducts: PropTypes.array.isRequired,
  showCallNow: PropTypes.bool,
  phoneNumber: PropTypes.string,
  isInternetAdded: PropTypes.bool,
  internetPlan: PropTypes.object,
  addIntToCart: PropTypes.func,
  dispatch: PropTypes.func,
  getAggregatedAddress: PropTypes.func,
  normalizedAddress: PropTypes.object,
  onResetAddress: PropTypes.func,
  serviceTerm: PropTypes.array,
  spanishLanguagePref: PropTypes.bool,
};

export default WhiPlans;
